import * as React from "react";

import { MoreHorizontal } from "@/shared/foundation/icons";
import { DropdownMenu, RobotoButton } from "@/shared/foundation/primitives";

import styles from "./RowActionsMenu.module.css";

/** One entry of a row's "…" menu. */
export interface RowAction {
  /** The item's text, and its key within the menu, so unique per row. */
  label: string;
  /** A leading icon, sized by the menu. */
  icon?: React.ReactNode;
  /** Runs when the item is chosen; the menu closes on its own. */
  onClick: () => void;
  /** Marks an action that destroys something; such items sort last. */
  danger?: boolean;
  /** Shows the item greyed out and ignores clicks on it. */
  disabled?: boolean;
}

interface RowActionsMenuProps {
  /** Names the row in the trigger's accessible label ("Actions for <label>"). */
  label: string;
  /** The row's items in display order, before danger items move last. */
  actions: readonly RowAction[];
}

/**
 * The trailing "…" menu of a list row. Ordinary actions come first and
 * destructive ones last, after a separator, so a slip of the pointer never
 * lands on Delete. Renders nothing when the row has no actions.
 */
export function RowActionsMenu({ label, actions }: RowActionsMenuProps) {
  if (actions.length === 0) {
    return null;
  }
  const ordinary = actions.filter((action) => action.danger !== true);
  const destructive = actions.filter((action) => action.danger === true);
  const renderItem = (action: RowAction) => (
    <DropdownMenu.Item
      key={action.label}
      icon={action.icon}
      variant={action.danger === true ? "danger" : "default"}
      disabled={action.disabled}
      onClick={action.onClick}
    >
      {action.label}
    </DropdownMenu.Item>
  );
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger
        render={
          <RobotoButton
            variant="ghost"
            size="sm"
            className={styles.trigger}
            icon={<MoreHorizontal />}
            aria-label={`Actions for ${label}`}
          />
        }
      />
      <DropdownMenu.Content align="end">
        {ordinary.map(renderItem)}
        {ordinary.length > 0 && destructive.length > 0 && (
          <DropdownMenu.Separator />
        )}
        {destructive.map(renderItem)}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
